import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '~/lib/api';

interface Application {
  _id: string;
  company: string;
  title: string;
  status: string;
  sent_at: string;
}

interface HistoryResponse {
  applications: Application[];
  total: number;
  page: number;
  totalPages: number;
}

const STATUS_LABELS: Record<string, string> = {
  SENT: 'Enviada',
  REPLIED: 'Con respuesta',
  INTERVIEW: 'Entrevista',
  REJECTED: 'Rechazada',
  QUEUED: 'En cola',
  NO_RESPONSE: 'Sin respuesta',
};

const STATUS_BADGE: Record<string, string> = {
  SENT: 'badge-info',
  REPLIED: 'badge-success',
  INTERVIEW: 'badge-accent',
  REJECTED: 'badge-error',
  QUEUED: 'badge-ghost',
  NO_RESPONSE: 'badge-warning',
};

const FILTER_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'SENT', label: 'Enviadas' },
  { value: 'REPLIED', label: 'Con respuesta' },
  { value: 'INTERVIEW', label: 'Entrevista' },
  { value: 'REJECTED', label: 'Rechazadas' },
  { value: 'QUEUED', label: 'En cola' },
  { value: 'NO_RESPONSE', label: 'Sin respuesta' },
];

const LIMIT = 20;

export default function JobApplyHistoryPage() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchApplications = useCallback(async (p: number, s: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(p),
        limit: String(LIMIT),
      });
      if (s) params.set('status', s);
      const res = await apiFetch(`/job-apply/api/applications?${params}`);
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data: HistoryResponse = await res.json();
      setApplications(data.applications);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.message || 'Error al cargar postulaciones.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications(page, status);
  }, [page, status, fetchApplications]);

  const onStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(e.target.value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Historial de postulaciones</h1>
        <select
          className="select select-bordered select-sm"
          value={status}
          onChange={onStatusChange}
        >
          {FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="alert alert-error text-sm">{error}</div>}

      <div className="card bg-base-100 shadow">
        <div className="card-body p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <span className="loading loading-spinner loading-lg text-primary" />
            </div>
          ) : applications.length === 0 ? (
            <p className="text-base-content/50 text-center py-12">
              {status
                ? 'No hay postulaciones con este estado.'
                : 'Aun no se han enviado postulaciones. Una vez configurada tu hoja de vida y preferencias, las postulaciones apareceran aqui.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Empresa</th>
                    <th>Posicion</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((app) => (
                    <tr key={app._id}>
                      <td className="font-medium">{app.company}</td>
                      <td>{app.title}</td>
                      <td>
                        <span
                          className={`badge badge-sm ${STATUS_BADGE[app.status] || 'badge-ghost'}`}
                        >
                          {STATUS_LABELS[app.status] || app.status}
                        </span>
                      </td>
                      <td className="text-base-content/70 text-sm">
                        {new Date(app.sent_at).toLocaleDateString('es-CO', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-base-content/50">
            {total} postulacion{total !== 1 ? 'es' : ''} en total
          </p>
          <div className="join">
            <button
              className="join-item btn btn-sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Anterior
            </button>
            <button className="join-item btn btn-sm btn-disabled">
              {page} / {totalPages}
            </button>
            <button
              className="join-item btn btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
